import React, { useRef, useState } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import { isWireframe } from '../../objects/guards';
import { svgToIcon } from '../../utils/svg';
import { type Instance as TippyInstance } from 'tippy.js/index';
import styles from '../../styles.module.css';
import Tool, {
  type IToolAvailableInput,
  type ToolButtonComponent,
} from '../Tool';
import ToolButton from '../../toolbars/ToolButton';
import ToolWireframe from './ToolWireframe';
import WhiteboardController from '../../controllers/WhiteboardController';
import wireframeClearIcon from '../../assets/icons/wireframeClearIcon.svg';
import wireframeIcon from '../../assets/icons/wireframeIcon.svg';
import { WIREFRAME_SELECTION_ERROR_MESSAGE } from '../../utils/tools';
import {
  IconSearchBox,
  RenderFaIcon,
  filterIconList,
  getIcon,
  iconList,
  tranlsateIconData,
} from '../../utils/icons';
import { type IconData } from '../../types';

interface PopupProps {
  currentIcon: IconData | null;
  setCurrentIcon: (icon: IconData | null) => void;
  location: string;
  setLocation: (location: string) => void;
  selection: any;
  hidePopup: () => void;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({
    currentIcon,
    setCurrentIcon,
    location,
    setLocation,
    selection,
    hidePopup,
  }) => {
    const controller = useController(WhiteboardController);
    const [filterText, setFilterText] = useState('');
    const supportedIconLocations = selection.wireframe.supportedIcons();
    // Width of icon location buttons container

    const setIcon = (iconData, location, stayOpen = false) => {
      setCurrentIcon(iconData);
      selection.wireframe.setIcon(
        location,
        iconData
          ? { prefix: iconData?.prefix, iconName: iconData?.iconName }
          : null
      );
      if (!stayOpen) hidePopup();
      Tool.toolActionComplete({ controller });
    };

    let icons: IconData[] = [];
    const allIcons = iconList();
    if (filterText.length > 0) {
      icons = filterIconList(filterText);
    } else {
      icons = allIcons;
    }

    return (
      <div
        className='tippy-scrollable-element'
        style={{
          overflow: 'hidden auto',
        }}
      >
        <div className={styles.toolbarRow}>
          <div className={styles.toolbarRow} style={{ width: 0 }}>
            {supportedIconLocations.length > 1 &&
              supportedIconLocations.map(iconLocation => {
                return (
                  <ToolButton
                    key={iconLocation}
                    active={location === iconLocation}
                    className={styles.iconButton}
                    onClick={() => {
                      setLocation(iconLocation);
                      setIcon(currentIcon, iconLocation, true);
                    }}
                    name='Icon location'
                  >
                    {iconLocation.replace('icon', '')}
                  </ToolButton>
                );
              })}
          </div>
          <ToolButton
            onClick={() => setIcon(null, location, false)}
            name='Clear icon'
          >
            {svgToIcon(wireframeClearIcon)}
          </ToolButton>
        </div>
        <IconSearchBox
          value={filterText}
          onChange={value => setFilterText(value)}
          style={{
            background: 'transparent',
            outline: 'none',
            boxShadow: 'none',
            border: 'solid',
            borderWidth: '1px 0',
            borderColor: '#F1F1F1',
            borderRadius: '0',
            padding: '4px 0',
          }}
          placeholder='Search for an icon'
          onClick={iconData => setIcon(iconData, location, false)}
          icons={icons}
          currentIcon={currentIcon}
          controller={controller}
        />
      </div>
    );
  }
);

const IconButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const tippyRef = useRef<TippyInstance>();
    const startLocation = selection.wireframe
      .supportedIcons()
      .find(location => selection.wireframe.itemIcon(location) != null);
    const [location, setLocation] = useState(startLocation);
    const iconData = selection.wireframe.itemIcon(location);
    const icon = iconData ? getIcon(tranlsateIconData(iconData)) : null;
    const [currentIcon, setCurrentIcon] = useState(icon);

    return (
      <ToolButton
        popup={
          <Popup
            currentIcon={currentIcon}
            setCurrentIcon={setCurrentIcon}
            location={location ?? 'iconLeft'}
            setLocation={setLocation}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tool={tool}
        tippyRef={tippyRef}
      >
        {currentIcon && <RenderFaIcon iconData={currentIcon} />}
        {!currentIcon && svgToIcon(wireframeIcon)}
      </ToolButton>
    );
  }
);

class ToolWireframeIcon extends ToolWireframe {
  static toolName = 'Icon';

  static available({ selection }: IToolAvailableInput) {
    return selection?.type === 'wireframe';
  }

  static buttonComponent() {
    return IconButton;
  }
}

export default ToolWireframeIcon;
